import { AnyAction } from '@reduxjs/toolkit';
import { DFDHeaders } from 'serverTypes/authentication';
import { CacheTypes } from '../mobileCaching';

export type Nullable<T> = T | null;
export type Clients =
  | 'amwell'
  | 'amwellAuthService'
  | 'audit'
  | 'binary'
  | 'billing'
  | 'booking'
  | 'ccd'
  | 'cost'
  | 'costLocal'
  | 'dispatcher'
  | 'eob'
  | 'events'
  | 'eventsGeneration'
  | 'financialAssistance'
  | 'getCare'
  | 'highPriorityItems'
  | 'imh'
  | 'insurance'
  | 'lowPriorityItems'
  | 'medications'
  | 'messaging'
  | 'mhpStatus'
  | 'myDoctors'
  | 'notifications'
  | 'orchestration'
  | 'profile'
  | 'preference'
  | 'provider'
  | 'scanning'
  | 'testResults'
  | 'tooltips'
  | 'tps'
  | 'triageGyant'
  | 'visitSummary'
  | 'uss'
  | 'webpay';

export interface ActionPayloadRequest {
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
  url?: string;
  data?: Record<string | number, any>;
  headers?: Record<string | number, any>;
  params?: Record<string | number, any>;
}

export interface ActionPayload<T = any> {
  cacheRetrievalSuccessAction?: string;
  cacheType?: CacheTypes;
  cacheUpdatedAt?: string;
  data?: T;
  errors?: string[] | Error[];
  client?: Clients;
  useRequestState?: boolean;
  requestStateSelector?: any;
  request?: ActionPayloadRequest;
}

export interface Action<T = any> extends AnyAction {
  type: string;
  payload?: ActionPayload<T>;
  error?: Error;
}

export interface ServiceError {
  code: string;
  errno: string;
  sqlMessage: string;
  sqlState: string;
  index: string;
  sql: string;
}

export interface Coordinate {
  latitude: number | string;
  longitude: number | string;
}
export interface LinkTo {
  pathname: string;
  search?: string;
  hash?: string;
  state?: Record<string, string>;
}

export enum AdapterSource {
  MHP = 'mhp',
  UTAH = 'utah'
}

export enum DispatcherAuthPrefix {
  AUTHENTICATED = '/u',
  PUBLIC = '/p',
  AUTHLITE = '/al'
}

export enum DispatcherCategoryService {
  APP_SETTINGS = '/app-settings-service',
  BILLING = '/billing-service',
  CONSUMER = '/consumer-service',
  DELIVER_CARE = '/deliver-care-service',
  HEALTH_RECORDS = '/health-record-service',
  GET_CARE = '/get-care-service',
  UTILITY = '/utility-service',
  SELF = '/self'
}

export enum DispatcherResource {
  ACTION_ITEMS = '/action-items',
  ALLERGIES = '/allergies',
  APP_SETTINGS = '/app-settings',
  ATTACHMENTS = '/attachments',
  AUDIT = '/audit',
  BILLS = '/bills',
  BINARY = '/binary-contents',
  CLINIC_DOCUMENTS = '/clinic-documents',
  CONDITIONS = '/conditions', // problems list
  CONSUMERS = '/consumers',
  COVIDSCREENING = '/self-tests/covid',
  EDUCATION = '/patient-education',
  ENCOUNTERS = '/encounters',
  ENCOUNTERS_ID = '/encounters/id',
  ESTIMATED_COSTS = '/estimated-costs',
  EOB = '/eob',
  FINANCIAL_ASSISTANCE = '/financial-assistance',
  GOALS = '/goals',
  HEALTH_ADVISORIES = '/health-advisories',
  HEALTH_TOOLS_INFO = '/health-tools-information',
  HIPAA = '/hipaa',
  HSA = '/hsa',
  IMAGING = '/imaging',
  IMMUNIZATIONS = '/immunizations',
  INSURANCES = '/insurances',
  KYRUUS = '/kyruus',
  CONSUMERS_ID_INSURANCE = '/consumers/id/insurance',
  LABS = '/labs',
  LOGGERS = '/loggers',
  MEDICAL_HISTORY = '/medical-history',
  MEDICAL_TEST_RESULTS = '/medical-test-results',
  MEDICATIONS = '/medications',
  MESSAGES = '/messages',
  MICROBIOLOGIES = '/microbiologies',
  PATHOLOGIES = '/pathologies',
  PAYMENT_PLAN = '/payment-plan',
  PAYMENTS = '/payments',
  PLAN_BENEFITS = '/plan-benefits',
  PPMCE_PRO = '/PPMCEpro', // Note: this is the same as PMMCE
  PROCEDURES = '/procedures',
  PREFERRED = '/preferences',
  SERVICE_GROUP = '/service-groups',
  SERVICE_NOW = '/service-now',
  SOCIAL_HISTORY = '/social-history',
  SYMPTOM_CHECKER = '/symptom-checker',
  UI_DATA = '/ui-data',
  UNIFIED_REDIRECT = '/unified-redirect',
  USER_SESSION = '/user-sessions',
  VISIT_PAY = '/visit-pay',
  VISIT_SUMMARIES = '/visit-summaries',
  VISIT_TYPES = '/visit-types',
  VITAL_SIGNS = '/vital-signs',
  VIDEO_CONSENT = '/video-visit-terms'
}

export enum DispatcherEndpoint {
  APPLICATIONS = '/applications',
  ACTION = '/action',
  CASES_ID = '/cases/id',
  CASE_DATA = '/case-data',
  CONDITIONS = '/conditions',
  COLLECTIONS_DETAILS = '/collections-details',
  DOWNLOAD = '/_download',
  ELIGIBILITY = '/eligibility',
  FAIL = '/_fake', // phony stub that'll always cause requests to fail. Use to trigger the _FAIL flow when we don't have what we need to make a successful request, e.g. missing mhpDetails.url
  FILTER = '/filter',
  GLOSSARY = '/glossary',
  HEALTH_PLAN_LIST = '/health-plans',
  HIGH_PRIORITY = '/high-priority',
  HIGH_PRIORITY_V2 = '/high',
  HISTORICAL = '/historical',
  ID = '/id',
  ID_DOWNLOAD = '/id/_download',
  ID_INSURANCE = '/id/insurance',
  ID_INSURANCE_SUMMARY = '/id/insurance/summary',
  ID_PROVIDERS = '/id/providers',
  ID_REFRESH = '/id/_refresh',
  ID_TRANSMIT = '/id/_transmit',
  LATEST = '/latest',
  LOW_PRIORITY = '/low-priority',
  NEW = '/new/_download',
  ORDER = '/order',
  PARAMETER = '/parameter',
  PREVIEW = '/preview',
  PROVIDERS = '/providers',
  PREFERRED_PROVIDERS = '/preferred-providers',
  SEARCH = '/_search',
  SELECT_HEALTH = '/sh',
  SESSION = '/session',
  SESSIONS = '/sessions',
  SETTINGS = '/settings',
  SIR = '/sir',
  SIR_EXISTS = '/sir/_exists',
  SIR_TRANSMIT = '/sir/_transmit',
  SNOOZE = '/snooze',
  SUMMARY = '/summary',
  TICKET = '/ticket',
  TOKEN = '/token',
  TRANSMIT = '/_transmit',
  UPLOAD = '/_upload',
  VISIT_SUMMARIES = '/visit-summaries'
}

export enum RequestState {
  INITIALIZED = 'INITIALIZED',
  REQUESTING = 'REQUESTING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

export interface KeyValueStringMap {
  [key: string]: string;
}

export interface GenericObject {
  [key: string]: any;
}

export type DFDHeadersObject = {
  [K in DFDHeaders]?: string | number;
};

export interface VisitSummaryDetailsReq {
  method: string;
  url: string;
  headers?: DFDHeadersObject;
}

export interface MhpDetails {
  url: string;
  contentType?: string;
  headers?: DFDHeadersObject;
}

export type Language = 'es' | 'en';
